import { AvailabilitySort } from "@b2bportal/rbc-activities-api";
import { logger } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  ActivityRatingValueEnum,
  toActivityRatingValue,
} from "@tenant/activities";
import type { ActivitiesConfig } from "../../../../context";
import type { ActivitiesState } from "../../../../store";
import { fetchActivities, initialActivitiesAvailabilityState } from "../index";
import type {
  ActivitiesAvailabilitySearchState,
  ActivitySearchDetails,
} from "../types";
import { fetchPlace } from "./api/fetchPlace";
import {
  parseActivityParams,
  type ActivitySearchQueryParamValues,
} from "~core-activities/utilities/activityUrlUtils";

/**
 * Parse URL search params to extract partial search data
 * Uses parseActivityParams utility for validation
 */
export const getPartialSearchFromURL = (
  urlParams: ActivitySearchQueryParamValues
): Omit<ActivitySearchDetails, "place"> | null => {
  if (urlParams.fromDate == null || urlParams.untilDate == null) {
    return null;
  }

  return {
    dateRange: {
      from: urlParams.fromDate,
      until: urlParams.untilDate,
    },
    travelers: urlParams.travelers,
  };
};

/**
 * Initialize the search state from URL parameters
 * Parses URL, fetches place data, and populates Redux state
 */
export const initializeSearchState = createAsyncThunk<
  ActivitiesAvailabilitySearchState,
  { searchParams: URLSearchParams; location: string },
  { state: ActivitiesState; rejectValue: { error: string } | string | unknown }
>(
  "activitiesAvailability/initializeSearchState",
  async ({ searchParams, location }, thunkAPI) => {
    // Parse and validate all URL parameters
    const urlParams = parseActivityParams(searchParams);
    const maybePartialRequestedSearch = getPartialSearchFromURL(urlParams);

    try {
      if (maybePartialRequestedSearch == null) {
        return thunkAPI.rejectWithValue("Dates are missing");
      }

      // Decode the location from URL path (e.g., "/activities/availability/New%20York" -> "New York")
      const locationPart = location.split("/").pop();
      const locationQuery = decodeURIComponent(
        locationPart != null && locationPart.length > 0 ? locationPart : ""
      );

      const place = await fetchPlace(locationQuery);

      if (place == null) {
        return thunkAPI.rejectWithValue("Error fetching place");
      }

      const requestedSearch: ActivitySearchDetails = {
        place,
        ...maybePartialRequestedSearch,
      };

      return {
        searchFormValues: requestedSearch,
        requestedSearch: requestedSearch,
        sort: urlParams.sortBy,
        filters: {
          ...initialActivitiesAvailabilityState.filters,
          ...(urlParams.filters.maxPrice != null && {
            maxPrice: urlParams.filters.maxPrice,
          }),
          ...(urlParams.filters.freeCancellation && {
            freeCancellation: urlParams.filters.freeCancellation,
          }),
          ...(urlParams.filters.rating != null && {
            rating: urlParams.filters.rating,
          }),
          ...(urlParams.filters.duration.length > 0 && {
            duration: urlParams.filters.duration,
          }),
          ...(urlParams.filters.startTime.length > 0 && {
            startTime: urlParams.filters.startTime,
          }),
          ...(urlParams.filters.userRating != null && {
            userRating: urlParams.filters.userRating,
          }),
        },
      };
    } catch (error) {
      logger.debug(`Error initializing activities search state. ${error}`);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Fetch activities based on the initialized search state
 */
export const initializeFetchActivities = createAsyncThunk<
  void,
  { config?: ActivitiesConfig } | void,
  { state: ActivitiesState; rejectValue: { error: string } }
>("activitiesAvailability/initializeFetchActivities", async (arg, thunkAPI) => {
  const activityState = thunkAPI.getState().activities;
  const { searchFormValues, filters, sort } =
    activityState.activitiesAvailability;

  if (searchFormValues == null) {
    return thunkAPI.rejectWithValue({ error: "Search is empty" });
  }

  const { place, dateRange, travelers } = searchFormValues;

  // Validate required fields before fetching
  if (place == null || dateRange.from == null || dateRange.until == null) {
    return thunkAPI.rejectWithValue({
      error: "Missing required search parameters",
    });
  }

  try {
    // Build refinement from filters in Redux state (may include filters from URL)
    const hasFilters =
      filters.duration.length > 0 ||
      filters.startTime.length > 0 ||
      filters.maxPrice !== Number.MAX_SAFE_INTEGER ||
      filters.rating !== ActivityRatingValueEnum.FourPointFive ||
      filters.freeCancellation ||
      filters.userRating != null;

    const refinement = hasFilters
      ? {
          duration: filters.duration,
          startTime: filters.startTime,
          tags: [], // Can't map category/tag names to objects yet
          ...(filters.maxPrice !== Number.MAX_SAFE_INTEGER && {
            price: { maxPriceUsd: filters.maxPrice },
          }),
          rating: {
            minTripadvisorRating: toActivityRatingValue(filters.rating),
          },
          sort: sort ?? AvailabilitySort.Recommended,
        }
      : undefined;

    thunkAPI.dispatch(
      fetchActivities({
        place,
        dateRange: {
          from: dateRange.from,
          until: dateRange.until,
        },
        travelers,
        sort,
        refinement,
      })
    );
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return thunkAPI.rejectWithValue({ error: errorMessage });
  }
});

export const initializeActivities = createAsyncThunk<
  void,
  {
    searchParams: URLSearchParams;
    location: string;
    config?: ActivitiesConfig;
  }
>("activitiesAvailability/initializeActivities", async (props, thunkAPI) => {
  try {
    const searchStateResult = await thunkAPI.dispatch(
      initializeSearchState(props)
    );

    if (initializeSearchState.rejected.match(searchStateResult)) {
      return thunkAPI.rejectWithValue({
        error: "Failed to initialize search state",
      });
    }

    thunkAPI.dispatch(initializeFetchActivities({ config: props.config }));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return thunkAPI.rejectWithValue({ error: errorMessage });
  }
});
